import React, {useEffect, useMemo} from 'react';

import {Observability} from '@stripe-internal/conversations-client';
import {RequestedAction} from '@stripe-internal/support-conversations-controller';

import {AppCloseHandler, AppConfig, AppProfile} from '../types';
import {AppContext} from '../contexts/AppContext';
import {AppImpl} from '../App';
import {ClientFetch, defaultClientFetch} from '../lib/util/clientFetch';
import {ConversationsUIProviders} from './ConversationsUIProviders';
import {ParentMessageBus} from '../lib/parentMessageBus/ParentMessageBus';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  handleRequestedAction?: (action: RequestedAction) => void;
  parentMessageBus?: ParentMessageBus;
  observability?: Partial<Observability>;
  children: React.ReactNode;
  onClose?: AppCloseHandler;
};

export function AppProvider({
  config,
  profile,
  clientFetch = defaultClientFetch(),
  handleRequestedAction,
  parentMessageBus,
  observability,
  onClose,
  children,
}: Props) {
  const app = useMemo(() => {
    return new AppImpl(
      config,
      profile,
      clientFetch,
      handleRequestedAction,
      parentMessageBus,
      onClose,
      observability,
    );
  }, [
    config,
    profile,
    clientFetch,
    handleRequestedAction,
    parentMessageBus,
    observability,
    onClose,
  ]);

  useEffect(() => {
    app.subscribeToParentMessageBus();
    return () => {
      app.unsubscribeFromParentMessageBus();
    };
  }, [app]);

  return (
    <AppContext.Provider value={app}>
      <ConversationsUIProviders>{children}</ConversationsUIProviders>
    </AppContext.Provider>
  );
}
